import React, { useEffect } from 'react';
import { tx, tr } from '../../utils/i18n';
import { routePath } from '../../i18n/routes';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { ArrowRight, Link, Users, TrendingUp, Shield, Target, Zap, Smartphone, Globe, Settings } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import Header from '../Header';
import Footer from '../Footer';
import { Toaster } from 'sonner';
import SEOHead from '../SEOHead';

const DeepLinkService = () => {
  const { t, currentLanguage } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Deep Link Technology Advertising",
    "description": "Technologie propriétaire de deep link pour connecter directement les consommateurs aux pages produits. ROI 8x vs traditional.",
    "provider": {
      "@type": "Organization",
      "name": "OptimHub"
    }
  };

  const features = [
    {
      icon: Link,
      title: tx(currentLanguage, 'Technologie Propriétaire', 'Proprietary Technology', 'Proprietäre Technologie'),
      description: tx(currentLanguage, 'Solution deep link développée en interne pour une performance optimale', 'In-house developed deep link solution for optimal performance', 'Intern entwickelte Deep-Link-Lösung für optimale Leistung')
    },
    {
      icon: Target,
      title: 'Deep Link Bidder',
      description: tx(currentLanguage, 'Système d\'enchères intelligent qui optimise en temps réel vos campagnes', 'Intelligent bidding system that optimizes your campaigns in real time', 'Intelligentes Bietersystem, das Ihre Kampagnen in Echtzeit optimiert')
    },
    {
      icon: Globe,
      title: tx(currentLanguage, 'Ciblage Contextuel', 'Contextual Targeting', 'Kontextuelles Targeting'),
      description: tx(currentLanguage, 'Ciblage basé sur le contexte et les comportements utilisateur avancés', 'Targeting based on context and advanced user behaviors', 'Targeting basierend auf Kontext und erweiterten Nutzerverhaltensweisen')
    }
  ];

  const benefits = [
    {
      icon: Smartphone,
      title: tx(currentLanguage, 'Expérience Mobile Fluide', 'Seamless Mobile Experience', 'Nahtlose Mobile Erfahrung'),
      description: tx(currentLanguage, 'Redirection directe vers les apps mobiles pour une conversion optimisée', 'Direct redirection to mobile apps for optimized conversion', 'Direkte Weiterleitung zu mobilen Apps für optimierte Conversion')
    },
    {
      icon: TrendingUp,
      title: tx(currentLanguage, 'Performance Maximisée', 'Maximized Performance', 'Maximierte Leistung'),
      description: tx(currentLanguage, 'Algorithmes d\'IA qui s\'adaptent en continu pour optimiser vos résultats', 'AI algorithms that continuously adapt to optimize your results', 'KI-Algorithmen, die sich kontinuierlich anpassen, um Ihre Ergebnisse zu optimieren')
    },
    {
      icon: Settings,
      title: tx(currentLanguage, 'Contrôle Total', 'Total Control', 'Totale Kontrolle'),
      description: tx(currentLanguage, 'Dashboard avancé pour piloter et optimiser vos campagnes en temps réel', 'Advanced dashboard to manage and optimize your campaigns in real time', 'Erweiterte Dashboard zur Verwaltung und Optimierung Ihrer Kampagnen in Echtzeit')
    }
  ];

  const stats = [
    { value: '95%', label: tx(currentLanguage, 'Précision Targeting', 'Targeting Precision', 'Targeting-Präzision') },
    { value: '2.5s', label: tx(currentLanguage, 'Temps de Redirection', 'Redirect Time', 'Weiterleitungszeit') },
    { value: '8x', label: tx(currentLanguage, 'ROI vs Traditional', 'ROI vs Traditional', 'ROI vs Traditional') }
  ];

  const techSpecs = [
    {
      title: tx(currentLanguage, 'Attribution Multi-Touch', 'Multi-Touch Attribution', 'Multi-Touch-Attribution'),
      description: tx(currentLanguage, 'Suivi précis du parcours client sur tous les points de contact', 'Precise tracking of customer journey across all touchpoints', 'Präzise Verfolgung der Customer Journey über alle Touchpoints')
    },
    {
      title: tx(currentLanguage, 'Optimisation Temps Réel', 'Real-Time Optimization', 'Echtzeit-Optimierung'),
      description: tx(currentLanguage, 'Ajustements automatiques basés sur les performances en cours', 'Automatic adjustments based on ongoing performance', 'Automatische Anpassungen basierend auf laufender Leistung')
    },
    {
      title: tx(currentLanguage, 'Cross-Device Tracking', 'Cross-Device Tracking', 'Cross-Device-Tracking'),
      description: tx(currentLanguage, 'Suivi unifié de l\'utilisateur sur tous ses appareils', 'Unified user tracking across all their devices', 'Einheitliche Nutzerverfolgung über alle Geräte')
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title={tr(currentLanguage, "Deep Link Technology | Proprietary Deep Linking Platform - OptimHub")}
        description={tr(currentLanguage, "Technologie propriétaire de deep link. Connexion directe aux pages produits, Deep Link Bidder IA, ciblage contextuel avancé. ROI 8x, précision 95%. Content commerce optimisé.")}
        keywords="deep link technology, deep linking platform, deep link bidder, contextual targeting, mobile deep linking, app deep links, attribution technology, content commerce deep link, product page optimization"
        ogImage="https://images.unsplash.com/photo-1611162618071-b39a2ec055fb?w=1200"
        routeKey="serviceDeepLink"
        lang={currentLanguage}
        schemaData={schemaData}
      />
      <Header />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-cyan-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-blue-100 text-blue-700 hover:bg-blue-200">
                {tx(currentLanguage, 'Technologie Avancée', 'Advanced Technology', 'Fortschrittliche Technologie')}
              </Badge>
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
                Deep Link Technology
                <span className="block text-blue-600 mt-2">
                  {tx(currentLanguage, 'Advertising', 'Advertising', 'Werbung')}
                </span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                {tx(currentLanguage, 'Notre technologie propriétaire de deep link connecte les marchands avec des consommateurs hautement engagés. Avec notre deep link bidder et ciblage contextuel avancé, maximisez vos conversions. En tant que traffic brokers, nous achetons du trafic au CPC et vous ne payez qu\'au CPA.', 'Our proprietary deep link technology connects merchants with highly engaged consumers. With our deep link bidder and advanced contextual targeting, maximize your conversions. As traffic brokers, we buy traffic at CPC and you only pay at CPA.', 'Unsere proprietäre Deep-Link-Technologie verbindet Händler mit hochengagierten Verbrauchern. Mit unserem Deep-Link-Bieter und fortschrittlichem kontextuellem Targeting maximieren Sie Ihre Conversions. Als Traffic-Broker kaufen wir Traffic zu CPC und Sie zahlen nur zu CPA.')
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={() => window.location.href = `${routePath('home', currentLanguage)}#contact`}
                  className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center"
                >
                  {tx(currentLanguage, 'Commencer maintenant', 'Get Started', 'Jetzt starten')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </button>
                <a 
                  href="mailto:contact@optimhub.com?subject=Demande de démo Deep Link Technology"
                  className="border-2 border-blue-200 text-blue-700 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors duration-200 inline-flex items-center justify-center"
                >
                  {tx(currentLanguage, 'Voir la démo', 'Watch Demo', 'Demo ansehen')}
                </a>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1611162618071-b39a2ec055fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHw0fHxkaWdpdGFsJTIwbWFya2V0aW5nfGVufDB8fHxibHVlfDE3NjE1NzczMjF8MA&ixlib=rb-4.1.0&q=85"
                alt="Deep Link Technology"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              {tx(currentLanguage, 'Technologie Deep Link Avancée', 'Advanced Deep Link Technology', 'Fortschrittliche Deep-Link-Technologie')}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {tx(currentLanguage, 'Notre solution propriétaire offre des performances inégalées grâce à l\'intelligence artificielle et aux algorithmes avancés.', 'Our proprietary solution delivers unmatched performance through artificial intelligence and advanced algorithms.', 'Unsere proprietäre Lösung liefert unübertroffene Leistung durch künstliche Intelligenz und fortschrittliche Algorithmen.')
              }
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-white to-blue-50/30">
                  <CardHeader className="text-center pb-4">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-200 transition-colors duration-300">
                      <IconComponent className="w-8 h-8 text-blue-600" />
                    </div>
                    <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-300">
                      {feature.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600 leading-relaxed text-center">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tech Specs Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              {tx(currentLanguage, 'Spécifications Techniques', 'Technical Specifications', 'Technische Spezifikationen')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {techSpecs.map((spec, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-xl font-bold text-gray-900 mb-4">{spec.title}</h3>
                <p className="text-gray-600 leading-relaxed">{spec.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              {tx(currentLanguage, 'Pourquoi Deep Link Technology ?', 'Why Deep Link Technology?', 'Warum Deep Link Technology?')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <IconComponent className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-cyan-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            {tx(currentLanguage, 'Prêt à Révolutionner Vos Campagnes ?', 'Ready to Revolutionize Your Campaigns?', 'Bereit, Ihre Kampagnen zu revolutionieren?')}
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            {tx(currentLanguage, 'Découvrez la puissance de notre technologie Deep Link propriétaire et transformez vos résultats publicitaires.', 'Discover the power of our proprietary Deep Link technology and transform your advertising results.', 'Entdecken Sie die Kraft unserer proprietären Deep Link-Technologie und transformieren Sie Ihre Werbeergebnisse.')
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => window.location.href = `${routePath('home', currentLanguage)}#contact`}
              className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-colors duration-200 flex items-center justify-center"
            >
              {tx(currentLanguage, 'Démarrer maintenant', 'Start Now', 'Jetzt starten')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </button>
            <button 
              onClick={() => window.location.href = `${routePath('home', currentLanguage)}#contact`}
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/10 transition-colors duration-200"
            >
              {tx(currentLanguage, 'Parler à un expert', 'Talk to an Expert', 'Mit Experten sprechen')}
            </button>
          </div>
        </div>
      </section>
      <Footer />
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#374151',
            border: '1px solid #e5e7eb',
          },
        }}
      />
    </div>
  );
};

export default DeepLinkService;